/**
 * SlippageSettings — collapsible panel for adjusting slippage tolerance.
 *
 * Shows an "Auto" button (0.5%), preset buttons (0.1%, 0.5%, 1.0%),
 * and a custom input field. Warns for extreme values.
 * Value is in basis points (bps) — 50 = 0.5%.
 * Closes on click-outside and Escape key.
 */

import { useState, useId, useRef, useEffect, type ChangeEvent } from "react";

const AUTO_BPS = 50;

const PRESETS = [
  { label: "0.1%", bps: 10 },
  { label: "0.5%", bps: 50 },
  { label: "1.0%", bps: 100 },
] as const;

interface SlippageSettingsProps {
  bps: number;
  onChange: (bps: number) => void;
}

export function SlippageSettings({ bps, onChange }: SlippageSettingsProps) {
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [isAuto, setIsAuto] = useState(true);
  const inputId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const isPreset = !isAuto && PRESETS.some((p) => p.bps === bps);
  const isCustom = !isAuto && !isPreset;
  const pctDisplay = (bps / 100).toFixed(2);

  const warningLow = bps > 0 && bps < 5;
  const warningHigh = bps > 500;

  const onCustomInput = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
      setCustomValue(v);
      const n = parseFloat(v);
      if (!isNaN(n) && n >= 0 && n <= 50) {
        setIsAuto(false);
        onChange(Math.round(n * 100));
      }
    }
  };

  const selectPreset = (presetBps: number) => {
    setIsAuto(false);
    onChange(presetBps);
    setCustomValue("");
  };

  const selectAuto = () => {
    setIsAuto(true);
    onChange(AUTO_BPS);
    setCustomValue("");
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[11px] text-[--text-subtle] hover:text-[--text] transition"
        aria-expanded={open}
        aria-label={`Slippage tolerance: ${isAuto ? "Auto" : pctDisplay + "%"}`}
      >
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
        <span className="num">{isAuto ? "Auto" : `${pctDisplay}%`}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-10 mt-2 w-[280px] rounded-[--radius] border border-[--border] bg-[--bg-elev] p-3 shadow-[--shadow-card]">
          <div className="mb-2 text-[12px] font-medium text-[--text]">
            Slippage tolerance
          </div>

          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={selectAuto}
              className={`rounded-[--radius-sm] px-2.5 py-1.5 text-[12px] font-semibold transition ${
                isAuto
                  ? "bg-[--accent] text-[--accent-on]"
                  : "bg-[--bg-inset] text-[--text-muted] hover:text-[--text] hover:bg-[--border]"
              }`}
            >
              Auto
            </button>
            {PRESETS.map((p) => (
              <button
                key={p.bps}
                type="button"
                onClick={() => selectPreset(p.bps)}
                className={`flex-1 rounded-[--radius-sm] px-2 py-1.5 text-[12px] font-semibold transition ${
                  !isAuto && bps === p.bps
                    ? "bg-[--accent] text-[--accent-on]"
                    : "bg-[--bg-inset] text-[--text-muted] hover:text-[--text] hover:bg-[--border]"
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="relative flex-1">
              <input
                id={inputId}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Custom"
                value={isCustom && customValue === "" ? pctDisplay : customValue}
                onChange={onCustomInput}
                onFocus={() => {
                  if (isCustom && customValue === "") {
                    setCustomValue(pctDisplay);
                  }
                }}
                className={`num w-full rounded-[--radius-sm] border px-2 py-1.5 text-center text-[12px] font-semibold bg-[--bg-inset] text-[--text] focus:outline-none focus:ring-1 focus:ring-[--ring] ${
                  isCustom ? "border-[--accent]" : "border-[--border]"
                }`}
                aria-label="Custom slippage percentage"
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[--text-subtle]">
                %
              </span>
            </div>
          </div>

          {warningLow && (
            <p className="mt-2 text-[11px] text-[--warning]">
              Low slippage may cause transactions to fail.
            </p>
          )}
          {warningHigh && (
            <p className="mt-2 text-[11px] text-[--warning]">
              High slippage increases risk of front-running.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
